import { ErrorLink } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';
import { getAuthCookieNames, isSecureCookieBrowserContext, SessionErrorKind, shouldAttemptSessionRecovery, } from '@repo/common';
import { classifyApolloSessionError } from './classify-apollo-session-error';
import { isMutationOperation } from './create-auth-error-link-helpers';
import { createMissingCsrfRenewLink } from './create-missing-csrf-renew-link';
import { renewSessionCsrf } from './session-recovery';
import { createSessionRecoveryTelemetryReporter, SessionRecoveryEvent, } from './session-recovery-telemetry';
/** Context flag for an operation that already renewed CSRF after a CSRF failure. */
const CSRF_RENEW_ATTEMPTED = 'csrfRenewAttempted';
/**
 * Better Auth owns the browser session: an authentication failure is surfaced to
 * the caller (route guards own the login redirect). This link renews a missing
 * CSRF cookie before an operation, renews a rejected one once and sends a query
 * again with it, and logs non-session errors.
 */
export function createAuthErrorLink(config) {
    const { apiUri, getCookie, logger, audience, onRecoveryEvent } = config;
    const cookieNames = getAuthCookieNames(audience, isSecureCookieBrowserContext());
    const emitRecoveryEvent = createSessionRecoveryTelemetryReporter(logger, onRecoveryEvent);
    let csrfRenewPromise = null;
    const renewCsrfOnce = async () => {
        if (csrfRenewPromise) {
            return csrfRenewPromise;
        }
        csrfRenewPromise = (async () => {
            emitRecoveryEvent(SessionRecoveryEvent.CsrfRenewStarted, { audience });
            const renewed = await renewSessionCsrf({ apiUri });
            emitRecoveryEvent(renewed ? SessionRecoveryEvent.CsrfRenewSucceeded : SessionRecoveryEvent.CsrfRenewFailed, {
                audience,
            });
            return renewed;
        })().finally(() => {
            csrfRenewPromise = null;
        });
        return csrfRenewPromise;
    };
    return [
        createMissingCsrfRenewLink({
            getCookie,
            csrfCookieName: cookieNames.csrfToken,
            renewCsrf: renewCsrfOnce,
        }),
        new ErrorLink(({ error, operation, forward }) => {
            if (!error) {
                return;
            }
            const sessionErrorKind = classifyApolloSessionError(error);
            if (sessionErrorKind === SessionErrorKind.Csrf && operation.getContext()[CSRF_RENEW_ATTEMPTED] !== true) {
                operation.setContext({ [CSRF_RENEW_ATTEMPTED]: true });
                // The rejected mutation did not run, but sending it again is left to the user.
                const retry = !isMutationOperation(operation);
                return new Observable(observer => {
                    let subscriber;
                    let cancelled = false;
                    void renewCsrfOnce().then(renewed => {
                        if (cancelled) {
                            return;
                        }
                        if (renewed && retry) {
                            subscriber = forward(operation).subscribe(observer);
                            return;
                        }
                        observer.error(error);
                    });
                    return () => {
                        cancelled = true;
                        subscriber?.unsubscribe();
                    };
                });
            }
            if (shouldAttemptSessionRecovery(sessionErrorKind)) {
                return;
            }
            if (sessionErrorKind === SessionErrorKind.Unknown || sessionErrorKind === SessionErrorKind.Transient) {
                logger.error('Non-auth session error occurred', error, {
                    operation: operation.operationName,
                    sessionErrorKind,
                });
            }
            return;
        }),
    ];
}
//# sourceMappingURL=create-auth-error-link.js.map